import React, { PropsWithChildren, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import getPropsFromChildren from "../utils/getPropsFromChildren";

type FullScreenContainerProps = {
  height: number;
};

const FullScreenContainer = styled.div<FullScreenContainerProps & { $imageMaxHeight: number }>`
  min-height: ${(props) => `${props.height}px`};
  display: flex;
  flex-direction: column;
  justify-content: start;
  width: 100%;
  .content {
    padding-bottom: 40px;
  }
  .cta {
    margin-top: auto;
    width: 100%;
    padding-bottom: 30px;
  }
  .imageContainer {
    img {
      width: 100%;
      max-height: ${(props) => props.$imageMaxHeight}px;
      object-fit: contain;
    }
  }
`;

export const CTAFoldContent: React.FC = ({ children }) => {
  return <>{children}</>;
};

export const CTAFoldImage = styled.img<{ $minHeight?: number }>`
  min-height: ${(props) => (props.$minHeight ? `${props.$minHeight}px` : undefined)};
`;

export const CTAFoldCTA: React.FC = ({ children }) => {
  return <>{children}</>;
};

export const CTAFoldPushedContent: React.FC = ({ children }) => {
  return <>{children}</>;
};

export type CTAFoldProps = PropsWithChildren<{ debug?: boolean }>;

export default function CTAFold({ children, debug = false }: CTAFoldProps) {
  const componentRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const pushedContentRef = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [pushedContentHeight, setPushedContentHeight] = useState(0);

  const propsFromChildren = getPropsFromChildren(
    children,
    {
      cta: CTAFoldCTA,
      image: CTAFoldImage,
      content: CTAFoldContent,
      pushedContent: CTAFoldPushedContent,
    },
    (output) => {
      if (!output.cta) return "CTAFoldCTA is a required child of CTAFold";
      if (!output.content) return "CTAFoldContent is a required child of CTAFold";
    }
  );

  const handleResize = () => {
    const parentTop = componentRef.current?.parentElement?.offsetTop || 0;
    const contentDivHeight = contentRef.current?.offsetHeight;
    const pushedContentDivHeight = pushedContentRef.current?.offsetHeight;
    setContentHeight(contentDivHeight || 0);
    setPushedContentHeight(pushedContentDivHeight || 0);
    setAvailableHeight(document.body.offsetHeight - parentTop);
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useLayoutEffect(() => {
    handleResize();
  });

  const Debug = () => (debug ? <p>Available height: {availableHeight}</p> : null);
  const imageMaxHeight = useMemo(() => {
    const minHeight = (propsFromChildren.image?.propTypes as any)?.minHeight;
    if (!minHeight) return 0;
    const calculatedMinHeight = availableHeight - (contentHeight + pushedContentHeight) - 100;
    if (calculatedMinHeight < minHeight) return minHeight;
    return calculatedMinHeight;
  }, [(propsFromChildren.image?.propTypes as any)?.minHeight, pushedContentHeight, contentHeight, availableHeight]);

  return (
    <div>
      <FullScreenContainer $imageMaxHeight={imageMaxHeight} height={availableHeight} ref={componentRef}>
        <div className="imageContainer">{propsFromChildren.image}</div>
        <div className="content" ref={contentRef}>
          <Debug />
          {propsFromChildren.content}
        </div>
        <div className="cta">{propsFromChildren.cta}</div>
      </FullScreenContainer>
      <div ref={pushedContentRef}>{propsFromChildren.pushedContent}</div>
    </div>
  );
}
